import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  ButtonGroup,
  Button,
  Chip,
  Typography,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
} from "recharts";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { QUDA_COLORS } from "@/config/colors";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";

const TEAL = QUDA_COLORS.TEAL_DARK;

const PIE_COLORS = [
  "#0e7490", "#0891b2", "#06b6d4", "#22d3ee",
  "#67e8f9", "#8b5cf6", "#a78bfa", "#c4b5fd",
  "#6d28d9", "#7c3aed",
];

const PERIOD_OPTIONS = [
  { label: "30d", value: 30 },
  { label: "6m", value: 180 },
  { label: "1y", value: 365 },
  { label: "All", value: "all" },
];

function fmtDate(iso) {
  if (!iso) return iso;
  const p = String(iso).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}` : iso;
}

const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTHS_FULL = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function fmtDateForPeriod(iso, days) {
  if (!iso) return iso;
  if (days <= 30) return fmtDate(iso);
  const p = String(iso).split("-");
  if (p.length < 2) return iso;
  const m = parseInt(p[1], 10) - 1;
  // 6m view: backend buckets by week (one point per Monday), so labeling by
  // month alone (e.g. "Mar 26") collapses every week in that month onto the
  // same tick text, which is what produced the repeating x-axis labels.
  // Include the day-of-month so each week gets a distinct label.
  if (days <= 180) {
    const d = parseInt(p[2], 10);
    return `${MONTHS_SHORT[m] ?? p[1]} ${Number.isNaN(d) ? "" : d}`.trim();
  }
  // 1y view: backend buckets by month, so one point per month and MMM YY is unique.
  return `${MONTHS_SHORT[m] ?? p[1]} ${p[0].slice(2)}`;
}

// Parse a "YYYY-MM-DD" string as a UTC date (avoids local-timezone day shifting).
function parseIsoDateUTC(iso) {
  const p = String(iso).split("-").map(Number);
  if (p.length < 3 || p.some((n) => Number.isNaN(n))) return null;
  return new Date(Date.UTC(p[0], p[1] - 1, p[2]));
}

function fmtDayMonthYear(date) {
  return `${date.getUTCDate()} ${MONTHS_SHORT[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

// Tooltip label: shows the full range a bar represents, since each bar is a
// bucket (day / week / month) rather than a single point in time.
function fmtTooltipLabel(iso, days) {
  const start = parseIsoDateUTC(iso);
  if (!start) return String(iso);

  if (days <= 30) {
    return `Date: ${fmtDayMonthYear(start)}`;
  }

  if (days <= 180) {
    // Weekly bucket - iso is the Monday of the week (backend groups via WEEKDAY()).
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 6);
    return `Week: ${fmtDayMonthYear(start)} – ${fmtDayMonthYear(end)}`;
  }

  // Monthly bucket - iso is the 1st of the month.
  return `Month: ${MONTHS_FULL[start.getUTCMonth()]} ${start.getUTCFullYear()}`;
}

// Percentage label rendered INSIDE each pie slice (white text), so labels never clip at
// the chart/card edges the way outside labels do. Hidden for tiny slices to avoid clutter.
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (!percent || percent < 0.05) return null;
  const RAD = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.6;
  const x = cx + r * Math.cos(-midAngle * RAD);
  const y = cy + r * Math.sin(-midAngle * RAD);
  return (
    <text x={x} y={y} fill="#fff" fontSize={11} fontWeight={700} textAnchor="middle" dominantBaseline="central">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

function StatCard({ label, value, sub }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid #e5e7eb",
        backgroundColor: "#f9fafb",
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
      }}
    >
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, color: TEAL, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

function RankList({ title, items, color }) {
  if (!items || items.length === 0) return (
    <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color, mb: 1 }}>{title}</Typography>
      <Typography variant="body2" color="textSecondary">No data in the last 30 days</Typography>
    </Box>
  );
  const max = items[0]?.count ?? 1;
  return (
    <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color, mb: 1.5 }}>{title}</Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {items.map((item, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", minWidth: 16, textAlign: "right" }}>{i + 1}.</Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{item.name || "—"}</Typography>
                <Typography variant="body2" sx={{ color, fontWeight: 700, ml: 1 }}>{item.count}</Typography>
              </Box>
              <Box sx={{ height: 4, borderRadius: 2, backgroundColor: "#e5e7eb", overflow: "hidden" }}>
                <Box sx={{ height: "100%", width: `${(item.count / max) * 100}%`, backgroundColor: color, borderRadius: 2 }} />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const ChemistQudaTab = ({ chemistUUID, isManager = false }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async (periodDays) => {
    if (!chemistUUID) return;
    try {
      setLoading(true);
      setError(null);
      const endpoint = isManager ? API_PATHS.MANAGER_CHEMIST_QUDA : API_PATHS.CHEMIST_QUDA;
      const res = await phappsRequest(endpoint, "GET", null, { ChemistUUID: chemistUUID, days: periodDays });
      setData(res);
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to load Quda data"));
    } finally {
      setLoading(false);
    }
  }, [chemistUUID, isManager]);

  useEffect(() => { fetchData(days); }, [fetchData, days]);

  const handlePeriodChange = (newDays) => {
    if (newDays === days) return;
    setDays(newDays);
    setData(null);
  };

  if (loading) return (
    <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
      <Typography color="textSecondary">Loading Quda data…</Typography>
    </Box>
  );

  if (error) return (
    <Box sx={{ py: 4, px: 2 }}>
      <Alert severity="warning">{error}</Alert>
    </Box>
  );

  if (!data) return null;

  const { Metrics, OrdersPerDay, TopItems, TopStaff, CategoryBreakdown } = data;
  const periodLabel = days === 30 ? "30 days" : days === 180 ? "6 months" : days === 365 ? "12 months" : "all time";
  const staffPieData = (TopStaff ?? []).filter((r) => r.count > 0).slice(0, 8);
  // Many buckets (e.g. multi-year all-time, monthly) → let the chart scroll horizontally
  // instead of cramming every bar into the visible width.
  const barCount = (OrdersPerDay ?? []).length;
  const chartMinWidth = barCount > 40 ? `${barCount * 32}px` : "100%";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Stat cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 1.5 }}>
        <StatCard
          label={`Total Orders (${periodLabel})`}
          value={Number(Metrics?.TotalOrders30d ?? 0).toLocaleString()}
        />
        <StatCard
          label="Avg / Day"
          value={Number(Metrics?.AvgOrdersPerDay ?? 0).toFixed(1)}
          sub={`over ${periodLabel}`}
        />
        <StatCard
          label="Dispensary"
          value={`${Number(Metrics?.DispensaryPct ?? 0).toFixed(1)}%`}
          sub={`${Number(Metrics?.TotalDispensary30d ?? 0).toLocaleString()} orders`}
        />
        <StatCard
          label="OTC"
          value={`${(100 - Number(Metrics?.DispensaryPct ?? 0)).toFixed(1)}%`}
          sub={`${Number(Metrics?.TotalOtc30d ?? 0).toLocaleString()} orders`}
        />
        <StatCard
          label="All-time orders"
          value={Number(Metrics?.TotalOrdersAllTime ?? 0).toLocaleString()}
        />
      </Box>

      {/* Row 1: Chart (left) + Top 10 items (right) */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
        <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)", display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TEAL }}>
              Orders per {days <= 30 ? "day" : days <= 180 ? "week" : "month"}
            </Typography>
            <ButtonGroup size="small" disableElevation>
              {PERIOD_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  onClick={() => handlePeriodChange(opt.value)}
                  variant={days === opt.value ? "contained" : "outlined"}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    px: 1.5,
                    py: 0.4,
                    borderColor: TEAL,
                    color: days === opt.value ? "#fff" : TEAL,
                    backgroundColor: days === opt.value ? TEAL : "transparent",
                    "&:hover": { backgroundColor: days === opt.value ? TEAL : `${TEAL}18` },
                  }}
                >
                  {opt.label}
                </Button>
              ))}
            </ButtonGroup>
          </Box>
          <Box sx={{ flex: 1, minHeight: 240, overflowX: "auto" }}>
            <Box sx={{ height: 240, minWidth: chartMinWidth }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={OrdersPerDay} margin={{ top: 4, right: 8, left: -20, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 9 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => fmtDateForPeriod(v, days)}
                  interval="preserveStartEnd"
                />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <ReTooltip
                  formatter={(v, name) => [v, name === "dispensary" ? "Dispensary" : "OTC"]}
                  labelFormatter={(v) => fmtTooltipLabel(v, days)}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                />
                <Legend
                  formatter={(v) => v === "dispensary" ? "Dispensary" : "OTC"}
                  iconSize={10}
                  wrapperStyle={{ fontSize: 11 }}
                />
                <Bar dataKey="dispensary" stackId="a" fill={TEAL} radius={[0, 0, 0, 0]} maxBarSize={28} />
                <Bar dataKey="otc" stackId="a" fill={QUDA_COLORS.TEAL_MID} radius={[2, 2, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
            </Box>
          </Box>
        </Box>

        <RankList title={`Top 10 items ordered (${periodLabel})`} items={TopItems} color={TEAL} />
      </Box>

      {/* Row 2: Category ranklist (left) + Category pie chart (right) */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
        <RankList title={`Orders by category (${periodLabel})`} items={CategoryBreakdown} color={TEAL} />

        {(CategoryBreakdown ?? []).length > 0 ? (
          <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TEAL, mb: 0.5 }}>
              Category distribution ({periodLabel})
            </Typography>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={CategoryBreakdown.slice(0, 8)}
                  dataKey="count"
                  nameKey="name"
                  cx="50%"
                  cy="46%"
                  outerRadius={72}
                  label={renderPieLabel}
                  labelLine={false}
                >
                  {CategoryBreakdown.slice(0, 8).map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} formatter={(v) => v} />
                <ReTooltip formatter={(v) => [v, "Orders"]} contentStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="body2" color="textSecondary">No category data.</Typography>
          </Box>
        )}
      </Box>

      {/* Top staff: rank list + pie chart side by side */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <RankList title={`Top staff by interactions (${periodLabel})`} items={TopStaff} color="#7c3aed" />

        {staffPieData.length > 0 ? (
          <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#7c3aed", mb: 0.5 }}>
              Staff share (interactions)
            </Typography>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={staffPieData}
                  dataKey="count"
                  nameKey="name"
                  cx="50%"
                  cy="46%"
                  outerRadius={72}
                  label={renderPieLabel}
                  labelLine={false}
                >
                  {staffPieData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Legend
                  iconSize={10}
                  wrapperStyle={{ fontSize: 11 }}
                  formatter={(v) => v}
                />
                <ReTooltip
                  formatter={(v) => [v, "Interactions"]}
                  contentStyle={{ fontSize: 11 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="body2" color="textSecondary">No staff data.</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChemistQudaTab;
